import { DateTime } from 'luxon';
import {
  getGuestBookButtonText,
  isDataSourceMemberBookable,
} from '../../shared/utils/guestBookButton';
import { getDeepLinkUrl } from './DeeplinkHelper';

function sortingAlgorithm(a, b) {
  const aActivity = a.activity ? a.activity[0].prefLabel : '';
  const bActivity = b.activity ? b.activity[0].prefLabel : '';
  if (aActivity < bActivity) {
    return 1;
  }
  if (aActivity > bActivity) {
    return -1;
  }
  return 0;
}

class FacilityHelper {
  static sortByActivity(facilities) {
    return facilities.sort(sortingAlgorithm);
  }

  static isInFilter(facility, filter) {
    const isInActivityFilter = this.isInActivityFilter(facility, filter);

    if (Object.keys(filter).length === 0) {
      return true;
    }

    return isInActivityFilter;
  }

  static hasTimeOfDayFilter(filter) {
    if (typeof filter.timeOfDay === 'undefined' || filter.timeOfDay === 'all') {
      return false;
    }
    switch (filter.timeOfDay) {
      default:
        return false;
      case 'morning':
      case 'afternoon':
      case 'evening':
        return true;
    }
  }

  static isInTimeOfDayFilter(subEvent, filter) {
    const startTime = DateTime.fromISO(subEvent.startDate);
    return this.isHourInTimeOfDayFilter(startTime.hour, filter);
  }

  static isHourInTimeOfDayFilter(startHour, filter) {
    if (typeof filter.timeOfDay === 'undefined' || filter.timeOfDay === 'all') {
      return true;
    } else {
      switch (filter.timeOfDay) {
        default:
          return true;
        case 'morning':
          if (startHour < 12) {
            return true;
          }
          break;
        case 'afternoon':
          if (startHour >= 12 && startHour < 17) {
            return true;
          }
          break;
        case 'evening':
          if (startHour >= 17) {
            return true;
          }
          break;
      }
    }
    return false;
  }

  static isWithinFilterParameters(isCategoryFilter, session, filter) {
    if (!isCategoryFilter) {
      return this.isInActivityFilter(session, filter);
    } else {
      return this.isInCategoryFilter(session, filter);
    }
  }

  static isInCategoryFilter(session, filter) {
    if (
      typeof filter.activity === 'undefined' ||
      !(filter.activity instanceof Array) ||
      filter.activity.length === 0 ||
      (filter.activity.length === 1 && !filter.activity[0])
    ) {
      return true;
    } else if (session.category instanceof Array && session.category.length) {
      for (const category of session.category) {
        if (filter.activity.includes(category)) {
          return true;
        }
      }
    }
    return false;
  }

  static isInActivityFilter(session, filter) {
    if (
      typeof filter.activity === 'undefined' ||
      !(filter.activity instanceof Array) ||
      filter.activity.length === 0 ||
      (filter.activity.length === 1 && !filter.activity[0])
    ) {
      return true;
    } else if (session.activity instanceof Array && session.activity.length) {
      for (const activity of session.activity) {
        if (activity.prefLabel) {
          if (filter.activity.indexOf(activity.prefLabel) >= 0) {
            return true;
          }
        }
      }
    }
    return false;
  }

  static getMinimumHour(slots) {
    let currentHour = 23;
    for (const slot of slots) {
      const startTime = DateTime.fromISO(slot.startDate);
      if (startTime.hour < currentHour) {
        currentHour = startTime.hour;
      }
    }
    return currentHour;
  }

  static checkoutUrl(checkoutBaseUrl, checkoutUrlTemplate, theme) {
    let url = checkoutUrlTemplate.replace(/\{checkoutBaseUrl\}/, checkoutBaseUrl);
    if (theme) {
      url = `${url}?theme=${theme}`;
    }
    return url;
  }

  static description(description) {
    if (description === null || description === undefined) {
      return '';
    }
    return description.replace(/^\s*[0-9]{2}:[0-9]{2}(\s*-\s*[0-9]{2}:[0-9]{2})?([:,;\s*])?/, '');
  }

  static filterSlotsByDate(slots, date) {
    return slots.filter((slots) => slots.startDate.match(new RegExp(date)));
  }

  static availabilityString(remainingUses, shouldShowNoAvailability) {
    if (shouldShowNoAvailability) {
      return null;
    }
    if (typeof remainingUses === 'number') {
      if (remainingUses === 0) {
        return 'Fully booked';
      } else if (remainingUses === 1) {
        return '1 space left';
      } else if (remainingUses > 0) {
        return `${remainingUses} spaces left`;
      }
    }
    return null;
  }

  /**
   * @param {object} args
   * @param {Record<string, any>} args.slot
   * @param {Record<string, any>} args.facilityUse
   * @param {string} args.baseCheckoutUrl
   * @param {string} args.branded
   * @param {string} args.checkoutTheme
   * @param {string[]} args.bookableProviders
   * @param {string} args.provider
   * @param {Record<string, string>} args.memberDeepLinks
   * @param {boolean} args.shouldShowBookAsGuestButton
   * @param {boolean} args.shouldShowBookAsMemberButton
   * @param {boolean} args.shouldShowNoAvailability
   */
  static slotBookSpaceObjects({
    slot,
    facilityUse,
    baseCheckoutUrl,
    branded,
    checkoutTheme,
    bookableProviders: bookableDataSources = [],
    provider: dataSource,
    memberDeepLinks,
    shouldShowBookAsGuestButton,
    shouldShowBookAsMemberButton,
    shouldShowNoAvailability,
  }) {
    // Regardless of bookability, if the session is in progress, it can't be booked
    if (slot.startDate < new Date().toISOString()) {
      return [
        {
          tag: 'span',
          className: 'inProgress',
          text: 'In Progress',
          availability: null,
        },
      ];
    }
    // Regardless of bookability, if the session is full, it can't be booked
    if (slot.remainingUses === 0) {
      return [
        {
          tag: 'span',
          className: 'spacesFull',
          text: this.availabilityString(slot.remainingUses, shouldShowNoAvailability),
          availability: null,
        },
      ];
    }
    // If there are spaces but the Data Source is not bookable, we can't render any booking buttons
    if (!bookableDataSources.includes(dataSource)) {
      return [
        {
          tag: 'span',
          className: 'spacesAvailable',
          text: this.availabilityString(slot.remainingUses, shouldShowNoAvailability),
          availability: null,
        },
      ];
    }

    // Two booking buttons can potentially be rendered: member and guest
    let slotBookSpaceObjects = [];

    // Guest
    // If the Data Source is bookable but there's no checkoutUrl, it cannot be booked by a guest for some reason (that
    // has been determined by Search)
    if (shouldShowBookAsGuestButton) {
      const isFull = slot.remainingUses === 0;
      const checkoutUrlTemplate = slot['imin:checkoutUrlTemplate'];
      const isAvailableToBook = baseCheckoutUrl && checkoutUrlTemplate;
      const isThisDataSourceMemberBookable = isDataSourceMemberBookable(
        memberDeepLinks,
        dataSource
      );
      const guestBookButtonText = getGuestBookButtonText({
        isFull,
        isAvailableToBook,
        isThisDataSourceMemberBookable,
      });
      if (isAvailableToBook) {
        const checkoutUrl = this.checkoutUrl(baseCheckoutUrl, checkoutUrlTemplate, checkoutTheme);
        slotBookSpaceObjects.push({
          tag: 'a',
          className: `button buttonBook ${branded}`,
          text: guestBookButtonText,
          attributes: {
            href: checkoutUrl,
            target: '_blank',
            rel: 'noopener noreferrer',
          },
          availability: this.availabilityString(slot.remainingUses, shouldShowNoAvailability),
        });
      } else {
        slotBookSpaceObjects.push({
          tag: 'span',
          className: 'unavailable',
          text: guestBookButtonText,
        });
      }
    }

    // Member
    if (shouldShowBookAsMemberButton) {
      const deepLinkUrl = getDeepLinkUrl(dataSource, memberDeepLinks, { slot, facilityUse });
      if (deepLinkUrl && shouldShowBookAsMemberButton) {
        slotBookSpaceObjects.push({
          tag: 'a',
          className: `button buttonBook memberBook ${branded}`,
          text: 'Login and Book',
          attributes: {
            href: deepLinkUrl,
            target: '_blank',
            rel: 'noopener noreferrer',
          },
          availability: this.availabilityString(slot.remainingUses, shouldShowNoAvailability),
        });
      }
    }

    return slotBookSpaceObjects;
  }

  static getActivity(facility) {
    if (facility.activity) {
      return facility.activity[0] && facility.activity[0].prefLabel;
    }
    return facility.category && facility.category[0];
  }

  /**
   * Filters slots by lookahead period
   * @param {object[]} slots
   * @param {string} date
   * @param {number} numberOfDaysLookahead
   * @returns {object[]}
   */
  static filterSlotsByLookahead(slots, date, numberOfDaysLookahead) {
    return slots.filter((slot) => {
      const slotDate = DateTime.fromISO(slot.startDate);
      const lookaheadDate = DateTime.fromISO(date).plus({ days: numberOfDaysLookahead });
      return slotDate <= lookaheadDate;
    });
  }

  /**
   * Sorts slots by start date
   * @param {object[]} slots
   * @returns {object[]}
   */
  static sortSlotsByStartDate(slots) {
    return slots.sort((a, b) => {
      const aDate = DateTime.fromISO(a.startDate);
      const bDate = DateTime.fromISO(b.startDate);
      return aDate.diff(bDate).toMillis();
    });
  }
}

export default FacilityHelper;
